import { getAccessToken } from './getAccessToken';
import moment from 'moment';

const environmentUrl = "https://orgea61b289.crm8.dynamics.com";

function normalizeGuid(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return String(value).replace(/[{}]/g, '');
}

export interface BackingMixingData {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string | null;
  cr3ea_tourstartdate: string;
  cr3ea_observedby: string | null;
  cr3ea_platform: string;
  cr3ea_productname: string;
  cr3ea_executivename: string;
  cr3ea_lineno: string | null;
  cr3ea_time: string | null;
  cr3ea_standard_mixing_time: string | null;
  cr3ea_mixing_time: string | null;
  cr3ea_mixing_out_time: string | null;
  cr3ea_water_temp: string | null;
  cr3ea_yeast_slurry_temp: string | null;
  cr3ea_oil_temp: string | null;
  cr3ea_ice_kg: string | null;
  cr3ea_dough_temp: string | null;
  cr3ea_dough_ph: string | null;
  cr3ea_dividing_weight_10: string | null;
  cr3ea_seed_visibility: string | null;
}

export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

export function collectBackingMixingRecord(
  cycleNum: number,
  productName: string,
  executiveName: string,
  lineNo: string | null,
  startTime: string | null,
  standardMixingTime: string | null,
  mixingTime: string | null,
  mixingOutTime: string | null,
  waterTemp: string | null,
  yeastSlurryTemp: string | null,
  oilTemp: string | null,
  iceKg: string | null,
  doughTemp: string | null,
  doughPh: string | null,
  dividingWeight10: string | null,
  seedVisibility: string | null,
  QualityTourId: string,
  UserName: string | null
): BackingMixingData {
  return {
    cr3ea_qualitytourid: normalizeGuid(QualityTourId),
    cr3ea_title: 'PPV_' + moment().format('MM-DD-YYYY'),
    cr3ea_cycle: `Cycle-${cycleNum}`,
    cr3ea_shift: sessionStorage.getItem("shiftValue") || "shift 1",
    cr3ea_tourstartdate: moment().format('MM-DD-YYYY'),
    cr3ea_observedby: UserName || null,
    cr3ea_platform: "Web",
    cr3ea_productname: productName || "N/A",
    cr3ea_executivename: executiveName || "N/A",
    cr3ea_lineno: lineNo || null,
    cr3ea_time: startTime || null,
    cr3ea_standard_mixing_time: standardMixingTime || null,
    cr3ea_mixing_time: mixingTime || null,
    cr3ea_mixing_out_time: mixingOutTime || null,
    cr3ea_water_temp: waterTemp || null,
    cr3ea_yeast_slurry_temp: yeastSlurryTemp || null,
    cr3ea_oil_temp: oilTemp || null,
    cr3ea_ice_kg: iceKg || null,
    cr3ea_dough_temp: doughTemp || null,
    cr3ea_dough_ph: doughPh || null,
    cr3ea_dividing_weight_10: dividingWeight10 || null,
    cr3ea_seed_visibility: seedVisibility || null,
  };
}

export async function savesectionApicall(
  data: BackingMixingData[],
  optionalAccessToken?: string
): Promise<SaveResponse> {
  try {
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = true);

    let accessToken: string | undefined = optionalAccessToken;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_mixings";
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results: any[] = [];
    for (const record of data) {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(record),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      const result = await response.json();
      results.push(result);
    }

    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: true,
      message: `${data.length} records saved successfully`,
      data: results,
    };
  } catch (error) {
    console.error("Error saving Backing Mixing records:", error);
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to save records. Please try again.",
      data: [],
    };
  }
}

export interface CycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNo: string;
  startTime: string;
  standardMixingTime: string;
  mixingTime: string;
  mixingOutTime: string;
  waterTemp: string;
  yeastSlurryTemp: string;
  oilTemp: string;
  iceKg: string;
  doughTemp: string;
  doughPh: string;
  dividingWeight10: string;
  seedVisibility: string;
}

export async function fetchCycleData(
  QualityTourId: string,
  optionalAccessToken?: string | null
): Promise<CycleData[]> {
  try {
    let accessToken: string | undefined = optionalAccessToken ?? undefined;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_mixings";
    const qualityTourGuid = normalizeGuid(QualityTourId);

    const selectFields = [
      "cr3ea_cycle",
      "cr3ea_productname",
      "cr3ea_executivename",
      "cr3ea_lineno",
      "cr3ea_time",
      "cr3ea_standard_mixing_time",
      "cr3ea_mixing_time",
      "cr3ea_mixing_out_time",
      "cr3ea_water_temp",
      "cr3ea_yeast_slurry_temp",
      "cr3ea_oil_temp",
      "cr3ea_ice_kg",
      "cr3ea_dough_temp",
      "cr3ea_dough_ph",
      "cr3ea_dividing_weight_10",
      "cr3ea_seed_visibility",
    ].join(",");

    const buildUrl = (filter: string) =>
      `${environmentUrl}/api/data/v${apiVersion}/${tableName}` +
      `?$filter=${encodeURIComponent(filter)}` +
      `&$select=${selectFields}`;

    let filter = `cr3ea_qualitytourid eq '${qualityTourGuid}'`;
    let response = await fetch(buildUrl(filter), { headers });

    if (!response.ok) {
      const errText = await response.text();
      if (errText.includes("Unrecognized") || errText.includes("Edm.") || errText.includes("$filter")) {
        filter = `_cr3ea_qualitytourid_value eq '${qualityTourGuid}'`;
        response = await fetch(buildUrl(filter), { headers });
        if (!response.ok) {
          throw new Error(`Failed to fetch data (retry): ${response.status} - ${await response.text()}`);
        }
      } else {
        throw new Error(`Failed to fetch data: ${response.status} - ${errText}`);
      }
    }

    const data = await response.json();
    const cycles: CycleData[] = [];

    (data.value || []).forEach((record: any) => {
      const cycleNum = (record.cr3ea_cycle || "").replace("Cycle-", "").trim();
      if (!cycleNum) return;

      cycles.push({
        cycleNum,
        product: record?.cr3ea_productname ?? "",
        executiveName: record?.cr3ea_executivename ?? "",
        lineNo: record?.cr3ea_lineno ?? "",
        startTime: record?.cr3ea_time ?? "",
        standardMixingTime: record?.cr3ea_standard_mixing_time ?? "",
        mixingTime: record?.cr3ea_mixing_time ?? "",
        mixingOutTime: record?.cr3ea_mixing_out_time ?? "",
        waterTemp: record?.cr3ea_water_temp ?? "",
        yeastSlurryTemp: record?.cr3ea_yeast_slurry_temp ?? "",
        oilTemp: record?.cr3ea_oil_temp ?? "",
        iceKg: record?.cr3ea_ice_kg ?? "",
        doughTemp: record?.cr3ea_dough_temp ?? "",
        doughPh: record?.cr3ea_dough_ph ?? "",
        dividingWeight10: record?.cr3ea_dividing_weight_10 ?? "",
        seedVisibility: record?.cr3ea_seed_visibility ?? "",
      });
    });

    return cycles;
  } catch (error) {
    console.error("Error fetching Backing Mixing cycle data:", error);
    return [];
  }
}
